import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type Column<T> = {
  key: string;
  /** The header from md up, and the label of the pair on a card. */
  label: string;
  cell: (row: T) => ReactNode;
  /** The card's title (the person, the model, the day). Not repeated as a pair. */
  primary?: boolean;
  /** Row controls: unlabelled, along the foot of the card. */
  actions?: boolean;
  align?: "right";
  /** Extra classes on the table cell; the card ignores them. */
  className?: string;
};

/** `md`, the header menu's breakpoint. */
const WIDE = "(min-width: 768px)";

function useWide(): boolean {
  const read = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(WIDE).matches : true);
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return wide;
}

/**
 * A table from md up; below it, one card per row, the primary column as the
 * title and the rest as label: value pairs, row controls at the foot. A wide
 * table in a sideways-scrolling box cut its columns off on a phone with
 * nothing to say it scrolled. `layout` forces one view (tests).
 */
export function ResponsiveTable<T>({
  rows,
  columns,
  rowKey,
  rowClassName,
  empty,
  footer,
  layout,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Classes for the row (`<tr>`) and the card alike, e.g. dimming. */
  rowClassName?: (row: T) => string | false | null | undefined;
  empty?: ReactNode;
  footer?: ReactNode;
  layout?: "table" | "cards";
}) {
  const wide = useWide();
  const cards = layout ? layout === "cards" : !wide;

  if (cards) {
    const primary = columns.filter((c) => c.primary);
    const pairs = columns.filter((c) => !c.primary && !c.actions);
    const actions = columns.filter((c) => c.actions);
    return (
      <div className="bg-card rounded-lg border">
        {rows.length === 0 && empty ? <p className="text-muted-foreground px-4 py-3 text-center text-xs">{empty}</p> : null}
        <ul className="divide-border divide-y">
          {rows.map((row) => (
            <li key={rowKey(row)} className={cn("min-w-0 px-4 py-3 text-sm", rowClassName?.(row))}>
              {primary.length > 0 ? (
                <h3 className="mb-1.5 min-w-0 font-medium break-words">
                  {primary.map((c) => (
                    <span key={c.key} className="block">
                      {c.cell(row)}
                    </span>
                  ))}
                </h3>
              ) : null}
              <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                {pairs.map((c) => (
                  <div key={c.key} className="contents">
                    <dt className="text-muted-foreground">{c.label}</dt>
                    <dd className="min-w-0 tabular-nums [overflow-wrap:anywhere]">{c.cell(row)}</dd>
                  </div>
                ))}
              </dl>
              {actions.length > 0 ? (
                <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                  {actions.map((c) => (
                    <div key={c.key}>{c.cell(row)}</div>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {footer}
      </div>
    );
  }

  return (
    <div className="bg-card overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-left text-xs">
          <tr className="border-border border-b">
            {columns.map((c) => (
              <th key={c.key} className={cn("px-3 py-2", c.align === "right" && "text-right")}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty ? (
            <tr>
              <td colSpan={columns.length} className="text-muted-foreground px-3 py-3 text-center text-xs">
                {empty}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => (
            <tr key={rowKey(row)} className={cn("border-border border-b last:border-0", rowClassName?.(row))}>
              {columns.map((c) => (
                <td key={c.key} className={cn("px-3 py-2", c.align === "right" && "text-right", c.className)}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
